import { FC, useState } from 'react'
import { Box, Chip, Collapse, Grow, Paper, Stack, Switch, FormControlLabel, TextField, Typography } from '@mui/material'
import SettingsIcon from '@mui/icons-material/Settings'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { FLOW } from './tokens'
import { WizardData } from './types'

interface SettingsNodeProps {
  data: WizardData
  onChange: (field: keyof WizardData, value: unknown) => void
  translate: (key: string) => string
}

const SettingsNode: FC<SettingsNodeProps> = ({ data, onChange, translate }) => {
  const [expanded, setExpanded] = useState(false)

  return (
    <Grow in timeout={400} style={{ transitionDelay: '200ms' }}>
      <Paper
        elevation={0}
        sx={{
          'width': FLOW.nodeWidth,
          'borderRadius': FLOW.cardRadius,
          'border': `1px solid ${FLOW.grayBorder}`,
          'borderLeft': '4px solid rgba(0,0,0,0.2)',
          'bgcolor': FLOW.grayBg,
          'overflow': 'hidden',
          'transition': 'all 0.2s cubic-bezier(0.4,0,0.2,1)',
          '&:hover': { borderColor: 'rgba(0,0,0,0.15)' },
        }}
      >
        <Box
          onClick={() => setExpanded(!expanded)}
          sx={{
            display: 'flex', alignItems: 'center', px: 2.5, py: 2, gap: 1.5,
            cursor: 'pointer', userSelect: 'none',
          }}
        >
          <Box sx={{
            width: 36, height: 36, borderRadius: '10px',
            bgcolor: 'rgba(0,0,0,0.06)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <SettingsIcon fontSize="small" sx={{ color: FLOW.gray }} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
              <Chip
                label={translate('automationRule.settings.label')}
                size="small"
                sx={{
                  height: 18, fontSize: '0.625rem', fontWeight: 700,
                  letterSpacing: '0.06em', bgcolor: 'rgba(0,0,0,0.06)',
                  color: FLOW.gray, borderRadius: '4px',
                }}
              />
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(0,0,0,0.82)' }}>
              {translate('automationRule.settings.subtitle')}
            </Typography>
          </Box>

          <KeyboardArrowDownIcon sx={{
            color: FLOW.gray, fontSize: 20,
            transition: 'transform 0.2s', transform: expanded ? 'rotate(180deg)' : 'none',
          }} />
        </Box>

        <Collapse in={expanded}>
          <Box sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
            <Stack spacing={2}>
              <TextField
                label={translate('data.cooldownMinutes')}
                type="number" size="small" fullWidth
                value={data.cooldown_minutes}
                onChange={(e) => onChange('cooldown_minutes', Number(e.target.value))}
                helperText={translate('automationRule.settings.cooldownHelp')}
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: '10px' },
                }}
              />
              <FormControlLabel
                control={<Switch checked={data.enabled} onChange={(e) => onChange('enabled', e.target.checked)} />}
                label={translate('data.enabled')}
              />
            </Stack>
          </Box>
        </Collapse>
      </Paper>
    </Grow>
  )
}

export default SettingsNode
